/**
 * Le terrain : ce que l'ouvrier voit, et ce qu'il fait remonter.
 *
 * Le patron ne peut pas être sur trois chantiers à la fois. Les ouvriers y
 * sont, eux — ce module les branche comme les seuls capteurs que l'entreprise
 * ait sur place.
 *
 * Toutes les règles de cloisonnement vivent en base, dans les policies posées
 * par `20260301001100` et `20260301001200`, et non ici. Ce n'est pas un
 * doublon manquant : un porteur de JWT appelle PostgREST sans passer par le
 * moindre service, donc une restriction écrite ici seulement ne garderait
 * rien. Les `allowedRoles` de ce fichier servent à rendre un refus lisible,
 * pas à le produire.
 */
import { z } from "zod";
import { ActionLevel } from "../../levels.js";
import { defineService } from "../../service.js";
import type { ExecutionContext } from "../../context.js";
import { sortFieldReports } from "./types.js";
import type { Assignment, CallContact, DayPart, FieldReport, Mission, MyPhase } from "./types.js";

// ─── Entrées ───────────────────────────────────────────────────────────────

const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Date attendue au format AAAA-MM-JJ");

export const readMissionsInput = z.object({
  confirmationId: z.string().optional(),
  /** Le jour dont on veut la mission. Aujourd'hui par défaut. */
  on: isoDate.optional(),
});

export const readUpcomingMissionsInput = z.object({
  confirmationId: z.string().optional(),
  /**
   * Combien de jours regarder devant. Bridé à 90 côté base plutôt qu'ici : la
   * borne doit tenir même si l'appel ne passe pas par ce schéma.
   */
  days: z.coerce.number().int().min(1).max(90).default(7),
});

export const listAssignmentsInput = z.object({
  confirmationId: z.string().optional(),
  projectId: z.string().uuid().optional(),
  employeeId: z.string().uuid().optional(),
  /**
   * Bornes de période, au sens du **chevauchement** et non de l'inclusion :
   * une affectation qui a commencé le mois dernier et court toujours concerne
   * cette semaine, alors que ni son début ni sa fin n'y tombent. Filtrer sur
   * l'inclusion viderait la grille du planning de ce qu'elle doit d'abord
   * montrer — les chantiers longs.
   */
  from: isoDate.optional(),
  to: isoDate.optional(),
});

export const assignToProjectInput = z.object({
  confirmationId: z.string().optional(),
  assignmentId: z.string().uuid().optional(),
  projectId: z.string().uuid(),
  employeeId: z.string().uuid(),
  startsOn: isoDate,
  /** Ouverte : sur un chantier long, personne ne connaît la fin le jour où
   *  l'on affecte l'équipe. */
  endsOn: isoDate.nullish(),
  roleOnSite: z.string().trim().max(80).nullish(),
  notes: z.string().trim().max(500).nullish(),
  /** Le week-end ne se travaille que si on l'a décidé. Voir `coversDay`. */
  worksSaturday: z.boolean().optional(),
  worksSunday: z.boolean().optional(),
  /** La journée entière par défaut. Voir `DayPart`. */
  dayPart: z.enum(["journee", "matin", "apres_midi"]).optional(),
});

export const removeAssignmentInput = z.object({
  confirmationId: z.string().optional(),
  assignmentId: z.string().uuid(),
});

export const reportFromFieldInput = z.object({
  confirmationId: z.string().optional(),
  projectId: z.string().uuid(),
  kind: z
    .enum(["material_shortage", "incident", "access", "defect", "other"])
    .default("material_shortage"),
  severity: z.enum(["info", "warning", "critical"]).default("warning"),
  message: z
    .string()
    .trim()
    .min(1, "Dites ce qui se passe : un signalement vide n'aide personne")
    .max(1000),
});

export const readMyPhasesInput = z.object({
  confirmationId: z.string().optional(),
  projectId: z.string().uuid("Identifiant de chantier attendu"),
});

export const declareProgressInput = z.object({
  confirmationId: z.string().optional(),
  phaseId: z.string().uuid("Identifiant de phase attendu"),
  percent: z.coerce
    .number({ message: "Un pourcentage est attendu" })
    .int("Un avancement s'exprime en points entiers")
    .min(0, "Un avancement ne peut pas être négatif")
    .max(100, "Un avancement ne dépasse pas 100 %"),
  /** Un mot du terrain. Le message par défaut se fabrique en base. */
  message: z.string().trim().max(2000, "Message trop long").optional(),
});

export const applyProgressInput = z.object({
  confirmationId: z.string().optional(),
  reportId: z.string().uuid("Identifiant de signalement attendu"),
});

export type ReadMyPhasesInput = z.infer<typeof readMyPhasesInput>;
export type DeclareProgressInput = z.infer<typeof declareProgressInput>;
export type ApplyProgressInput = z.infer<typeof applyProgressInput>;

export const listFieldReportsInput = z.object({
  confirmationId: z.string().optional(),
  /** Par défaut, seuls ceux qui attendent encore quelque chose. */
  includeResolved: z.boolean().default(false),
  projectId: z.string().uuid().optional(),
});

export const resolveFieldReportInput = z.object({
  confirmationId: z.string().optional(),
  reportId: z.string().uuid(),
  status: z.enum(["acknowledged", "resolved"]),
});

export type ReadMissionsInput = z.infer<typeof readMissionsInput>;
export type ReadUpcomingMissionsInput = z.infer<typeof readUpcomingMissionsInput>;
export type ListAssignmentsInput = z.infer<typeof listAssignmentsInput>;
export type AssignToProjectInput = z.infer<typeof assignToProjectInput>;
export type RemoveAssignmentInput = z.infer<typeof removeAssignmentInput>;
export type ReportFromFieldInput = z.infer<typeof reportFromFieldInput>;
export type ListFieldReportsInput = z.infer<typeof listFieldReportsInput>;
export type ResolveFieldReportInput = z.infer<typeof resolveFieldReportInput>;

// ─── Port ──────────────────────────────────────────────────────────────────

export interface SaveAssignmentCommand {
  assignmentId?: string;
  projectId: string;
  employeeId: string;
  startsOn: string;
  endsOn: string | null;
  roleOnSite: string | null;
  notes: string | null;
  worksSaturday: boolean;
  worksSunday: boolean;
  dayPart: DayPart;
}

export interface CreateFieldReportCommand {
  projectId: string;
  kind: FieldReport["kind"];
  severity: FieldReport["severity"];
  message: string;
}

export interface FieldRepository {
  myMissions(ctx: ExecutionContext, on: string): Promise<Mission[]>;
  /** Les mêmes, mais devant soi. Voir `my_upcoming_missions` côté base. */
  myUpcomingMissions(ctx: ExecutionContext, days: number): Promise<Mission[]>;

  listAssignments(
    ctx: ExecutionContext,
    filter: { projectId?: string; employeeId?: string; from?: string; to?: string },
  ): Promise<Assignment[]>;
  saveAssignment(ctx: ExecutionContext, cmd: SaveAssignmentCommand): Promise<Assignment>;
  removeAssignment(ctx: ExecutionContext, assignmentId: string): Promise<void>;

  createReport(ctx: ExecutionContext, cmd: CreateFieldReportCommand): Promise<FieldReport>;
  listReports(
    ctx: ExecutionContext,
    filter: { includeResolved: boolean; projectId?: string },
  ): Promise<FieldReport[]>;
  setReportStatus(
    ctx: ExecutionContext,
    reportId: string,
    status: "acknowledged" | "resolved",
  ): Promise<FieldReport>;

  /**
   * Les phases d'un chantier, telles que l'appelant a le droit de les voir.
   *
   * Passe par `my_project_phases()` en base : `project_phases` est fermée à
   * l'ouvrier, et la RLS filtre des lignes et jamais des colonnes.
   */
  myPhases(ctx: ExecutionContext, projectId: string): Promise<MyPhase[]>;

  /** Inscrit une déclaration d'avancement. Ne touche pas à la phase. */
  declareProgress(
    ctx: ExecutionContext,
    cmd: { phaseId: string; percent: number; message: string | null },
  ): Promise<FieldReport>;

  /** Applique une déclaration à sa phase et clôt le signalement, atomiquement. */
  applyDeclaredProgress(ctx: ExecutionContext, reportId: string): Promise<void>;

  /**
   * Les personnes joignables depuis le chantier.
   *
   * Passe par `my_call_contacts()` : `users` porte le rôle de chacun, donc la
   * carte des droits de l'entreprise, et un ouvrier ne la lit pas.
   */
  myCallContacts(ctx: ExecutionContext): Promise<CallContact[]>;
}

// ─── Services ──────────────────────────────────────────────────────────────

export function createFieldServices(
  repo: FieldRepository,
  now: () => Date = () => new Date(),
) {
  const today = () => now().toISOString().slice(0, 10);

  const mesMissions = defineService<ReadMissionsInput, Mission[]>({
    name: "mes_missions",
    level: ActionLevel.Read,
    handler: (ctx, input) => repo.myMissions(ctx, input.on ?? today()),
  });

  const mesProchainesMissions = defineService<ReadUpcomingMissionsInput, Mission[]>({
    name: "mes_prochaines_missions",
    level: ActionLevel.Read,
    handler: (ctx, input) => repo.myUpcomingMissions(ctx, input.days),
  });

  /**
   * Qui appeler depuis le chantier.
   *
   * Aucun rôle exigé : tout salarié appelle son patron, c'est le sens même du
   * bouton. La base borne déjà la réponse à sa propre entreprise, et à trois
   * champs.
   */
  const mesContacts = defineService<Record<string, never>, CallContact[]>({
    name: "mes_contacts",
    level: ActionLevel.Read,
    handler: (ctx) => repo.myCallContacts(ctx),
  });

  const listerAffectations = defineService<ListAssignmentsInput, Assignment[]>({
    name: "lister_affectations",
    level: ActionLevel.Read,
    handler: (ctx, input) =>
      repo.listAssignments(ctx, {
        ...(input.projectId ? { projectId: input.projectId } : {}),
        ...(input.employeeId ? { employeeId: input.employeeId } : {}),
        ...(input.from ? { from: input.from } : {}),
        ...(input.to ? { to: input.to } : {}),
      }),
  });

  const affecterAuChantier = defineService<AssignToProjectInput, Assignment>({
    name: "affecter_au_chantier",
    // Réversible : une affectation se corrige ou se retire, et rien n'est
    // parti à l'extérieur.
    level: ActionLevel.ReversibleWrite,
    allowedRoles: ["patron", "conducteur", "assistant"],
    handler: (ctx, input) =>
      repo.saveAssignment(ctx, {
        ...(input.assignmentId ? { assignmentId: input.assignmentId } : {}),
        projectId: input.projectId,
        employeeId: input.employeeId,
        startsOn: input.startsOn,
        endsOn: input.endsOn ?? null,
        roleOnSite: input.roleOnSite ?? null,
        notes: input.notes ?? null,
        // `?? false` et non la valeur par défaut du schéma : les services ne
        // valident pas leur entrée, et un appel qui omet le champ doit tomber
        // sur « pas de week-end », jamais sur `undefined` écrit en base.
        worksSaturday: input.worksSaturday ?? false,
        worksSunday: input.worksSunday ?? false,
        dayPart: input.dayPart ?? "journee",
      }),
  });

  const retirerAffectation = defineService<RemoveAssignmentInput, void>({
    name: "retirer_affectation",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: ["patron", "conducteur", "assistant"],
    handler: (ctx, input) => repo.removeAssignment(ctx, input.assignmentId),
  });

  const signalerDuTerrain = defineService<ReportFromFieldInput, FieldReport>({
    name: "signaler_du_terrain",
    // N2 et non N3 : rien ne sort de l'entreprise. Un signalement se corrige,
    // se requalifie, se clôt. Le soumettre à confirmation ferait exactement
    // l'inverse de ce qu'on cherche — un ouvrier qui doit attendre l'accord du
    // patron pour dire qu'il manque des parpaings ne le dira pas.
    level: ActionLevel.ReversibleWrite,
    handler: (ctx, input) =>
      repo.createReport(ctx, {
        projectId: input.projectId,
        kind: input.kind,
        severity: input.severity,
        message: input.message,
      }),
  });

  const listerSignalements = defineService<ListFieldReportsInput, FieldReport[]>({
    name: "lister_signalements",
    level: ActionLevel.Read,
    handler: async (ctx, input) => {
      const reports = await repo.listReports(ctx, {
        includeResolved: input.includeResolved,
        ...(input.projectId ? { projectId: input.projectId } : {}),
      });
      // Le plus grave d'abord, puis le plus ancien : ce qui dort depuis trois
      // jours est le vrai problème.
      return sortFieldReports(reports);
    },
  });

  const traiterSignalement = defineService<ResolveFieldReportInput, FieldReport>({
    name: "traiter_signalement",
    level: ActionLevel.ReversibleWrite,
    // Celui qui constate n'est pas celui qui décide que c'est traité. La base
    // l'impose aussi, par la policy `managers_resolve`.
    allowedRoles: ["patron", "conducteur", "assistant"],
    handler: (ctx, input) => repo.setReportStatus(ctx, input.reportId, input.status),
  });

  /**
   * Les phases du chantier où je suis affecté.
   *
   * N1 : savoir où en est l'ouvrage sur lequel on travaille n'est pas un
   * secret. Aucun `allowedRoles` — c'est la fonction en base qui borne, et
   * elle borne sur l'affectation plutôt que sur le rôle : un conducteur passe
   * par la fiche chantier, qui lui montre tout.
   */
  const mesPhases = defineService<ReadMyPhasesInput, MyPhase[]>({
    name: "mes_phases",
    level: ActionLevel.Read,
    handler: (ctx, input) => repo.myPhases(ctx, input.projectId),
  });

  /**
   * Déclarer l'avancement d'une phase, depuis le terrain.
   *
   * N2, et surtout **pas** une écriture sur la phase. C'est la décision prise
   * avec le patron : depuis que la file du soir compare l'avancement à la part
   * facturée, un chiffre tapé sur un téléphone mène à une proposition de
   * facture. Le laisser devenir l'avancement fermerait la chaîne
   * « un compagnon saisit 80 % → une situation de 18 000 € apparaît » sans
   * qu'aucun humain ne l'ait regardée.
   *
   * Le téléphone reste pourtant le seul capteur que l'entreprise ait sur ses
   * chantiers. D'où la forme : il déclare, ça se range dans les signalements,
   * et un responsable applique.
   */
  const declarerAvancement = defineService<DeclareProgressInput, FieldReport>({
    name: "declarer_avancement",
    level: ActionLevel.ReversibleWrite,
    handler: (ctx, input) =>
      repo.declareProgress(ctx, {
        phaseId: input.phaseId,
        percent: input.percent,
        message: input.message ?? null,
      }),
  });

  /**
   * Faire d'une déclaration l'avancement officiel.
   *
   * Le clic qui manque à l'autre bout de la chaîne. Réservé au bureau, comme
   * `traiter_signalement` et pour la même raison : celui qui constate n'est
   * pas celui qui décide que c'est acquis. La base l'impose aussi — `apply_
   * declared_progress` est SECURITY INVOKER, donc la policy de
   * `project_phases` s'applique à l'appelant.
   */
  const validerAvancement = defineService<ApplyProgressInput, void>({
    name: "valider_avancement_declare",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: ["patron", "conducteur", "assistant"],
    handler: (ctx, input) => repo.applyDeclaredProgress(ctx, input.reportId),
  });

  return {
    mesMissions,
    mesPhases,
    declarerAvancement,
    validerAvancement,
    mesProchainesMissions,
    mesContacts,
    listerAffectations,
    affecterAuChantier,
    retirerAffectation,
    signalerDuTerrain,
    listerSignalements,
    traiterSignalement,
  };
}

export type FieldServices = ReturnType<typeof createFieldServices>;
